// Derived device state for the 2026-09 redesign.
//
// The redesigned screens show five device states: Available, Deployed,
// Assigned, RMA and Quarantine. The underlying data has no single field
// carrying those — it has several orthogonal ones:
//
//   status        the bench/lifecycle state (STATUSES in App.js): Not Started,
//                 In Testing, Passed, Failed, Quarantine, Pending Retirement,
//                 Retired, Pending RMA, With Manufacturer
//   location      where the unit is booked to: a site name, or one of the
//                 system locations (In Stock / In Testing / Quarantine) or the
//                 pseudo-locations Employee Assignment / Pending RMA /
//                 With Manufacturer / Retired
//   availability  set by the mobile shift workflow only
//                 (components/deployment/): available, deployed, maintenance,
//                 rma, lost
//
// Both are still written and read by the bench test, RMA, dispatch and
// retirement flows, which are out of scope for the redesign. So rather than
// migrate the data, the five states are DERIVED here for display only.
// Nothing in this module writes; callers keep using status/availability.
//
// IMPORTANT — two parallel data models feed Deployed and Assigned, and a
// device is only ever in one of them:
//
//   desktop  dispatch sets device.location to the site name; employee sign-out
//            sets location "Employee Assignment" and pushes to st.assignments.
//            Neither touches device.availability.
//   mobile   the shift scan sheets set availability "deployed" and push an
//            open record to st.allocations. Neither touches device.location.
//
// Reading only the mobile fields (as this module first did) left every
// desktop-dispatched device counted as In stock and every desktop sign-out
// counted as nothing, which is what the dashboard tiles were showing in
// production. Both models must be consulted.

export const DEVICE_STATES = ["Available", "Deployed", "Assigned", "RMA", "Quarantine"];

// Tone per state, matching the status-pill set in owl-bench-components.css.
export const DEVICE_STATE_TONE = {
  Available:  "success",
  Deployed:   "info",
  Assigned:   "brand",
  RMA:        "danger",
  Quarantine: "warning",
};

// Locations that mean "not out in the field". SYS_LOCS in utils/locations.js
// covers only the three bench locations; these pseudo-locations are resolved
// by earlier branches of deviceState() and must not fall through to Deployed.
const NON_SITE_LOCATIONS = [
  "In Stock", "In Testing", "Quarantine",
  "Employee Assignment", "Pending RMA", "With Manufacturer",
  "Retired", "Pending Retirement",
];

// Callers may pass either the allocations array (legacy) or the whole state
// object. The state form is needed to see st.assignments, which is where
// desktop employee sign-outs live.
function sources(arg) {
  if (Array.isArray(arg)) return { allocations: arg, assignments: [] };
  if (arg && typeof arg === "object") {
    return {
      allocations: Array.isArray(arg.allocations) ? arg.allocations : [],
      assignments: Array.isArray(arg.assignments) ? arg.assignments : [],
    };
  }
  return { allocations: [], assignments: [] };
}

// A device is "Assigned" when it is out with a named staff member. That comes
// from either model:
//   - st.allocations   an open shift allocation (out, not yet returned)
//   - st.assignments   an open employee assignment, i.e. the assignment is not
//                      closed AND this device is not in returnedDeviceIds.
//                      A partly-returned assignment stays open, so the
//                      per-device check is what decides, not returnedAt alone.
export function isAssigned(device, arg) {
  if (!device) return false;
  const { allocations, assignments } = sources(arg);

  if (allocations.some(a => a && a.deviceId === device.id && a.status === "out" && !a.inAt)) {
    return true;
  }

  return assignments.some(a =>
    a && !a.returnedAt &&
    Array.isArray(a.deviceIds) && a.deviceIds.includes(device.id) &&
    !(a.returnedDeviceIds || []).includes(device.id)
  );
}

// Resolve a device to one of DEVICE_STATES.
//
// Order matters: the states are not mutually exclusive in the data, so this
// reports the most operationally significant one first. A quarantined unit is
// quarantined even if its availability still reads "available".
export function deviceState(device, arg = []) {
  if (!device) return "Available";

  const status = device.status || "";
  const location = device.location || "";
  const availability = device.availability || "available";

  // Quarantine and RMA are lifecycle facts that override physical location.
  if (status === "Quarantine" || location === "Quarantine") return "Quarantine";
  if (status === "Pending RMA" || status === "With Manufacturer" ||
      location === "Pending RMA" || location === "With Manufacturer" ||
      availability === "rma") return "RMA";

  // Out with a named person takes precedence over a stale availability value.
  if (isAssigned(device, arg)) return "Assigned";

  // Mobile model.
  if (availability === "deployed") return "Deployed";

  // Desktop model: booked to a real site rather than a bench or pseudo
  // location. This is the same rule the QC "currently" counts use.
  if (location && !NON_SITE_LOCATIONS.includes(location)) return "Deployed";

  return "Available";
}

// Count devices by state. Returns an object keyed by every state, so callers
// can render a full set of tiles without guarding for missing keys.
export function deviceStateCounts(devices = [], arg = []) {
  const counts = DEVICE_STATES.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
  devices.forEach(d => { counts[deviceState(d, arg)] += 1; });
  return counts;
}
